'use client';

import { memo, useEffect, useRef, useState } from 'react';
import { Play } from 'lucide-react';
import { cn } from '@/lib/utils';
import type { PortfolioVideoEntry } from '@/lib/types/portfolio-video';
import { VideoEmbed } from './video-embed';
// Singleton : une seule requête réseau même si HeroVideoCarousel et
// VideoShowcase montent au même instant. La promise est partagée au niveau
// du module (pas du composant) et ne repart pas si la page n'est pas rechargée.
let _portfolioPromise: Promise<PortfolioVideoEntry[]> | null = null;
function portfolioVideosFetch(): Promise<PortfolioVideoEntry[]> {
  if (!_portfolioPromise) {
    _portfolioPromise = fetch('/api/public/portfolio-videos')
      .then(r => r.ok ? r.json() : [])
      .catch(() => []);
  }
  return _portfolioPromise;
}



// Mur vidéo diagonal (façon Moggo / Youdji) : colonnes de vignettes 9/16
// inclinées à -8°, qui défilent verticalement en continu dans des directions
// alternées. Sur desktop le mur DÉBORDE de sa boîte (overflow visible) et
// n'est recadré que par la section hero elle-même (overflow-hidden), pour un
// effet pleine hauteur. Sur mobile il reste contenu et arrondi pour ne pas
// recouvrir le texte. Décoratif (aria-hidden).
const COLUMNS = 3;
const TILES_PER_COLUMN = 5;
const MAX_LIVE_VIDEOS = COLUMNS * TILES_PER_COLUMN;
const PLACEHOLDER_LABELS = [
  "Beauty", "Mode", "Lifestyle", "Food", "Sport",
  "Animaux", "B2B", "Santé", "Tech", "Voyage",
  "Gaming", "Déco", "Kids", "Auto", "Finance",
];
const AUTO_SCROLL_PX_PER_SEC = 26;
// Directions par colonne : le contenu monte (+1) ou descend (-1).
const COLUMN_DIRECTIONS = [1, -1, 1];
// Décalage initial (fraction de la hauteur d'une vignette) pour casser
// l'alignement horizontal des jointures entre colonnes.
const COLUMN_STAGGER = [0.35, 0.85, 0.6];

function useInView<T extends HTMLElement>(rootMargin = '100px') {
  const ref = useRef<T | null>(null);
  const [inView, setInView] = useState(false);
  useEffect(() => {
    const el = ref.current;
    if (!el || inView) return;
    const observer = new IntersectionObserver(
      ([entry]) => { if (entry.isIntersecting) setInView(true); },
      { rootMargin }
    );
    observer.observe(el);
    return () => observer.disconnect();
  }, [inView, rootMargin]);
  return { ref, inView };
}

function WallTile({ entry, label }: { entry: PortfolioVideoEntry | null; label: string | null }) {
  const [loaded, setLoaded] = useState(false);
  const { ref, inView } = useInView<HTMLDivElement>();

  return (
    <div
      ref={ref}
      className="relative flex-shrink-0 w-full rounded-[1.5rem] overflow-hidden shadow-2xl bg-white/5 border border-white/15"
      style={{ aspectRatio: "9/16" }}
    >
      {entry ? (
        <>
          {!loaded && (
            <div className="absolute inset-0 flex items-center justify-center bg-gradient-to-br from-primary/50 to-secondary/50 animate-pulse">
              <Play className="h-5 w-5 text-white/50 ml-0.5" />
            </div>
          )}
          <VideoEmbed
            entry={entry}
            shouldPlay={inView}
            onReady={() => setLoaded(true)}
            className={cn(
              "absolute inset-0 w-full h-full object-cover transition-opacity duration-500",
              loaded ? "opacity-100" : "opacity-0"
            )}
          />
        </>
      ) : (
        <div className="absolute inset-0 flex items-center justify-center bg-gradient-to-br from-primary via-primary/80 to-secondary">
          <div className="w-10 h-10 rounded-full bg-white/15 backdrop-blur-sm border border-white/30 flex items-center justify-center">
            <Play className="h-4 w-4 text-white/80 ml-0.5" />
          </div>
        </div>
      )}
      <div className="absolute inset-0 bg-gradient-to-t from-black/70 via-black/10 to-transparent pointer-events-none" />
      {label && (
        <div className="absolute bottom-2.5 inset-x-0 text-center">
          <span className="inline-block px-2 py-0.5 rounded-full bg-black/40 backdrop-blur-sm text-white text-[10px] font-bold uppercase tracking-wider">
            {label}
          </span>
        </div>
      )}
    </div>
  );
}

// memo() : ce composant réordonne ses vignettes en manipulant le DOM
// directement (déplacement du premier/dernier nœud de chaque colonne) pour ne
// jamais recréer l'iframe d'une vraie vidéo. Un re-render déclenché par un
// état sans rapport ailleurs sur la page (ex: la frappe dans le formulaire de
// contact) reconstruirait le DOM depuis le dernier rendu React connu et
// annulerait ce réordonnancement.
function HeroVideoCarousel({ className }: { className?: string }) {
  const [liveVideos, setLiveVideos] = useState<PortfolioVideoEntry[]>([]);
  const columnRefs = useRef<(HTMLDivElement | null)[]>([]);
  const parallaxRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    portfolioVideosFetch()
      .then(setLiveVideos)
      .catch(() => {});
  }, []);

  // Parallaxe souris légère : le mur glisse doucement à l'inverse du curseur
  // (couche interne au plan incliné : la rotation Tailwind reste intacte).
  useEffect(() => {
    const el = parallaxRef.current;
    if (!el) return;
    if (window.matchMedia('(prefers-reduced-motion: reduce)').matches) return;
    let raf = 0;
    function onMove(e: MouseEvent) {
      if (raf) return;
      raf = requestAnimationFrame(() => {
        raf = 0;
        const x = (e.clientX / window.innerWidth - 0.5) * 18;
        const y = (e.clientY / window.innerHeight - 0.5) * 18;
        if (el) el.style.transform = `translate(${-x}px, ${-y}px)`;
      });
    }
    window.addEventListener('mousemove', onMove);
    return () => {
      window.removeEventListener('mousemove', onMove);
      if (raf) cancelAnimationFrame(raf);
    };
  }, []);

  // Défilement vertical continu par colonne, sans contenu dupliqué : dès
  // qu'une vignette sort complètement par le haut (colonne montante) ou par
  // le bas (colonne descendante), son vrai nœud DOM est déplacé à l'autre
  // extrémité et scrollTop est compensé d'exactement sa hauteur dans le même
  // tick, invisible à l'œil. Même technique que VideoShowcase, à la verticale.
  useEffect(() => {
    const columns = columnRefs.current.filter(Boolean) as HTMLDivElement[];
    if (columns.length === 0) return;
    const reduceMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;

    // Décalage initial pour casser l'alignement des jointures.
    columns.forEach((col, c) => {
      const first = col.firstElementChild as HTMLElement | null;
      if (first) {
        col.scrollTop = first.getBoundingClientRect().height * (COLUMN_STAGGER[c % COLUMN_STAGGER.length]);
      }
    });

    if (reduceMotion) return;

    const TICK_MS = 30;
    const stepPx = (AUTO_SCROLL_PX_PER_SEC * TICK_MS) / 1000;

    const intervalId = window.setInterval(() => {
      columns.forEach((col, c) => {
        if (col.scrollHeight <= col.clientHeight) return;
        const gap = parseFloat(getComputedStyle(col).rowGap || '0');
        const dir = COLUMN_DIRECTIONS[c % COLUMN_DIRECTIONS.length];

        if (dir > 0) {
          // Le contenu monte : la première vignette finit par sortir en haut.
          col.scrollTop += stepPx;
          const first = col.firstElementChild as HTMLElement | null;
          if (first) {
            const h = first.getBoundingClientRect().height + gap;
            const maxScroll = col.scrollHeight - col.clientHeight;
            if (col.scrollTop >= Math.min(h, maxScroll)) {
              col.appendChild(first);
              col.scrollTop -= h;
            }
          }
        } else {
          // Le contenu descend : on recycle la dernière vignette vers le haut
          // AVANT que scrollTop n'atteigne 0 (il ne peut pas être négatif).
          if (col.scrollTop <= stepPx) {
            const last = col.lastElementChild as HTMLElement | null;
            if (last) {
              const h = last.getBoundingClientRect().height + gap;
              col.insertBefore(last, col.firstElementChild);
              col.scrollTop += h;
            }
          }
          col.scrollTop -= stepPx;
        }
      });
    }, TICK_MS);

    return () => window.clearInterval(intervalId);
  }, [liveVideos.length]);

  // Répartition round-robin des vraies vidéos sur les colonnes pour qu'elles
  // soient visibles partout, pas concentrées dans la première colonne.
  const columns: { entry: PortfolioVideoEntry | null; label: string | null }[][] = Array.from(
    { length: COLUMNS },
    (_, c) =>
      Array.from({ length: TILES_PER_COLUMN }, (_, row) => {
        const i = row * COLUMNS + c;
        const entry = i < MAX_LIVE_VIDEOS ? liveVideos[i] ?? null : null;
        return {
          entry,
          label: entry ? (entry.category ?? null) : PLACEHOLDER_LABELS[i % PLACEHOLDER_LABELS.length],
        };
      })
  );

  return (
    <div className={cn("relative overflow-hidden lg:overflow-visible", className)} aria-hidden="true">
      {/* Plan incliné, volontairement plus grand que le cadre. Sur desktop il
          déborde largement en haut, en bas et à droite : c'est la section
          hero (overflow-hidden) qui le recadre à ses propres bords. */}
      <div className="absolute -inset-x-16 -inset-y-24 rotate-[-8deg] lg:-inset-y-56 lg:-left-10 lg:-right-48">
        <div ref={parallaxRef} className="h-full transition-transform duration-300 ease-out will-change-transform">
          <div className="flex h-full justify-center gap-3 sm:gap-4">
            {columns.map((tiles, c) => (
              <div
                key={c}
                ref={(el) => { columnRefs.current[c] = el; }}
                className="flex h-full w-36 flex-shrink-0 flex-col gap-3 overflow-hidden sm:w-40 sm:gap-4 lg:w-52"
              >
                {tiles.map((t, i) => (
                  <WallTile key={i} entry={t.entry} label={t.label} />
                ))}
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* Fondus sur les 4 bords (mobile uniquement : sur desktop le mur est
          recadré net par les bords de la section, façon Moggo) */}
      <div className="pointer-events-none absolute inset-x-0 top-0 z-10 h-16 bg-gradient-to-b from-background to-transparent lg:hidden" />
      <div className="pointer-events-none absolute inset-x-0 bottom-0 z-10 h-16 bg-gradient-to-t from-background to-transparent lg:hidden" />
      <div className="pointer-events-none absolute inset-y-0 left-0 z-10 w-10 bg-gradient-to-r from-background to-transparent sm:w-14 lg:hidden" />
      <div className="pointer-events-none absolute inset-y-0 right-0 z-10 w-10 bg-gradient-to-l from-background to-transparent sm:w-14 lg:hidden" />
    </div>
  );
}

export default memo(HeroVideoCarousel);
